import { useEffect, useRef, useState } from "react";
import {
  resourcesLossOfControl,
  resourcesWorkLifeBalance,
} from "@/features/mentor/data/resources";
import {
  DyteAudioVisualizer,
  DyteAvatar,
  DyteCameraToggle,
  DyteClock,
  DyteDialogManager,
  DyteLeaveButton,
  DyteMicToggle,
  DyteNameTag,
  DyteParticipantsAudio,
  DyteParticipantTile,
} from "@dytesdk/react-ui-kit";
import { provideDyteDesignSystem, Transcript, UIConfig } from "@dytesdk/ui-kit";
import type DyteClient from "@dytesdk/web-core";
import { DyteParticipant } from "@dytesdk/web-core";
import { Lightbulb, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useUpsertMeeting } from "../../../../../hooks/useMeeting";
import { useLiveMeetingAnalysis } from "../../../../../hooks/useMeetingAnalysis";
import { useUpsertMeetingTranscript } from "../../../../../hooks/useMeetingTranscript";
import { CustomStates, SetStates } from "../../../../../types/dyte";
import { SessionTranscripts } from "../../../../../types/supabase";
import AlertSection from "../components/AlertSection";
import ChoicesSection from "../components/ChoicesSection";
import Resources from "../components/Resources";

function RemoteView({
  meeting,
  config,
  states,
  setStates,
  sessionId,
  meetingTranscript,
}: {
  meeting: DyteClient;
  config: UIConfig;
  states: CustomStates;
  setStates: SetStates;
  sessionId: string;
  conversationPrompts: string[];
  meetingTranscript: SessionTranscripts;
}) {
  const navigate = useNavigate();

  const localUser = meeting.self;

  const { data: meetingAnalysis } = useLiveMeetingAnalysis(sessionId);
  const { mutate: upsertTranscript } = useUpsertMeetingTranscript();
  const { mutate: upsertMeeting } = useUpsertMeeting();
  const [remoteParticipant, setRemoteParticipant] =
    useState<DyteParticipant | null>(null);

  const speakerMapRef = useRef(new Map<string, string>());

  // Initialize mentor mapping when meeting is available
  useEffect(() => {
    if (meeting?.self?.name) {
      speakerMapRef.current.set(meeting.self.name, "MENTOR");
    }
  }, [meeting?.self?.name]);

  console.log("MEETING ANALYSIS: ", meetingAnalysis);
  console.log("MEETING TRANSCRIPT: ", meetingTranscript);

  useEffect(() => {
    const activeParticipants = meeting.participants.active.toArray();
    const initialRemoteParticipant = activeParticipants[0];
    if (initialRemoteParticipant) {
      setRemoteParticipant(initialRemoteParticipant);
      speakerMapRef.current.set(initialRemoteParticipant.name, "MENTEE");
    }

    const handleParticipantJoined = (participant: DyteParticipant) => {
      console.log("Participant joined: ", participant);
      setRemoteParticipant(participant);
      speakerMapRef.current.set(participant.name, "MENTEE");
    };

    const handleParticipantLeft = (participant: DyteParticipant) => {
      console.log("Participant left: ", participant);
      setRemoteParticipant(null);
    };

    meeting.participants.joined.on(
      "participantJoined",
      handleParticipantJoined
    );
    meeting.participants.active.on("participantLeft", handleParticipantLeft);

    return () => {
      meeting.participants.active.removeListener(
        "participantJoined",
        handleParticipantJoined
      );
      meeting.participants.active.removeListener(
        "participantLeft",
        handleParticipantLeft
      );

      meeting.leaveRoom();
    };
  }, [meeting]);

  useEffect(() => {
    const handleTranscript = async (transcript: Transcript) => {
      // @ts-ignore
      if (!transcript.isPartialTranscript) {
        console.log(
          "Session ID: ",
          sessionId,
          "Transcript: ",
          transcript.transcript,
          transcript.name
        );

        console.log("Pushing transcript: ", transcript.transcript);
        console.log("Speaker: ", speakerMapRef.current.get(transcript.name));

        meetingTranscript.content.push({
          transcript: transcript.transcript,
          speaker: speakerMapRef.current.get(transcript.name) || "MENTOR",
          timestamp: transcript.date.toISOString(),
        });

        upsertTranscript(meetingTranscript);
      }
    };

    meeting.ai.on("transcript", handleTranscript);

    return () => {
      meeting.ai.removeListener("transcript", handleTranscript);
    };
  }, []);

  return (
    <div
      className="flex h-[calc(100vh-64px)] flex-col"
      ref={(el) => {
        if (!el) return;

        provideDyteDesignSystem(el, {
          theme: "light",
        });

        el?.addEventListener("dyteStateUpdate", (e) => {
          const { detail: newStateUpdate } = e as unknown as {
            detail: CustomStates;
          };
          setStates((oldState: CustomStates) => {
            return {
              ...oldState,
              ...newStateUpdate,
            };
          });
        });
      }}
    >
      {/* Alert Banner */}
      <AlertSection alert={meetingAnalysis?.content?.alert || ""} />

      <div className="flex flex-1">
        {/* Left Sidebar - Key Topics */}
        <div className="w-1/6 bg-white p-4">
          <ChoicesSection
            choices={meetingAnalysis?.content?.key_topics || []}
            title="Key Topics"
            defaultText="Key topics will appear here..."
            icon={ListChecks}
            variant="rounded"
          />
        </div>

        {/* Main Content - Mentee Video */}
        <div className="flex h-full w-4/6 flex-col">
          <div className="relative flex-1">
            {/* Timer */}
            <div className="absolute left-4 top-4 z-10 rounded bg-gray-700 px-2 py-1 text-white">
              <DyteClock meeting={meeting} className="h-auto text-white" />
            </div>

            {/* Mentee Video */}
            <div className="size-full p-4">
              {remoteParticipant ? (
                <div className="size-full rounded-xl border border-vincer-100 bg-vincer-100/50">
                  <DyteParticipantTile
                    participant={remoteParticipant}
                    meeting={meeting}
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "transparent",
                      borderRadius: "12px",
                    }}
                  >
                    <DyteNameTag
                      participant={remoteParticipant}
                      meeting={meeting}
                    >
                      <DyteAudioVisualizer participant={remoteParticipant} />
                    </DyteNameTag>
                    <DyteAvatar size="sm" participant={remoteParticipant} />
                  </DyteParticipantTile>
                </div>
              ) : (
                <div className="flex size-full flex-col items-center justify-center rounded-xl bg-vincer-100/50">
                  <p className="font-medium text-vincer-900">
                    Mentee is joining...
                  </p>
                </div>
              )}
            </div>

            {/* Video Controls */}
            <div className="p2 absolute bottom-4 left-1/2 flex w-3/5 -translate-x-1/2 items-center justify-center space-x-2 rounded-full bg-white/80 px-6 shadow">
              <DyteMicToggle size="md" meeting={meeting} variant="horizontal" />
              <DyteCameraToggle
                size="md"
                meeting={meeting}
                variant="horizontal"
              />
              <DyteLeaveButton
                size="md"
                variant="horizontal"
                onClick={async () => {
                  console.log("ENDING SESSION, SESSION ID: ", sessionId);
                  await upsertMeeting({
                    uuid: sessionId,
                    status: "ended",
                  });

                  await meeting.leave().then(() => {
                    console.log("LEAVING MEETING, SESSION ID: ", sessionId);
                    navigate(`/mentor/post-session?sessionId=${sessionId}`);
                  });
                }}
              />
            </div>
          </div>

          {/* Suggestions Section at Bottom */}
          <div className="bg-white p-4">
            <ChoicesSection
              choices={meetingAnalysis?.content?.mentor_suggestions || []}
              title="Suggestions"
              defaultText="Suggestions will appear here..."
              icon={Lightbulb}
              variant="arrow"
            />
          </div>
        </div>

        {/* Right Sidebar - Mentor Video & Resources */}
        <div className="flex w-1/6 flex-col gap-4 bg-white p-4">
          {/* Mentor Video */}
          <div className="mb-4 rounded-xl bg-vincer-700/90">
            <DyteParticipantTile
              participant={localUser}
              meeting={meeting}
              style={{
                borderRadius: "12px",
                width: "100%",
                aspectRatio: "16/9",
                backgroundColor: "transparent",
              }}
            >
              <DyteNameTag participant={localUser} meeting={meeting}>
                <DyteAudioVisualizer participant={localUser} />
              </DyteNameTag>
              <DyteAvatar size="sm" participant={localUser} />
            </DyteParticipantTile>
          </div>

          {/* Resources */}
          <Resources
            resources={
              meetingAnalysis?.content?.stressor_confidence?.label ===
              "loss_of_control"
                ? resourcesLossOfControl
                : meetingAnalysis?.content?.stressor_confidence?.label ===
                    "work_life_balance"
                  ? resourcesWorkLifeBalance
                  : []
            }
          />
        </div>
      </div>

      <DyteParticipantsAudio meeting={meeting} />
      <DyteDialogManager meeting={meeting} config={config} states={states} />
    </div>
  );
}

export default RemoteView;
